/**
 * 状态管理 - TikTok 登录状态
 */
import { reactive, computed } from 'vue'
import { TikTokAPI, TokenManager } from '../api/tiktok'

// 全局状态
const state = reactive({
  // 用户信息
  user: null,
  // 登录状态
  isLoggedIn: false,
  // 加载状态
  loading: false,
  // 错误信息
  error: null,
  // 健康状态
  healthStatus: null
})

// 计算属性
const getters = {
  isAuthenticated: computed(() => state.isLoggedIn && !!state.user),
  userDisplayName: computed(() => state.user?.display_name || '未知用户'),
  userAvatar: computed(() => state.user?.avatar_url || ''),
  hasError: computed(() => !!state.error)
}

// 方法
const actions = {
  /**
   * 初始化状态 - 检查本地存储的登录信息
   */
  async initialize() {
    state.loading = true
    state.error = null

    try {
      // 检查是否已登录
      if (TokenManager.isLoggedIn()) {
        const userInfo = TokenManager.getUserInfo()
        if (userInfo) {
          state.user = userInfo
          state.isLoggedIn = true
        } else {
          // 尝试获取用户信息
          await this.fetchUserInfo()
        }
      }
    } catch (error) {
      console.error('初始化状态失败:', error)
      state.error = error.message
      this.logout()
    } finally {
      state.loading = false
    }
  },

  /**
   * 发起 TikTok 登录
   */
  async initiateLogin() {
    state.loading = true
    state.error = null

    try {
      const result = await TikTokAPI.initiateLogin()
      
      if (result.success) {
        console.log('发起登录成功:' + JSON.stringify(result.data))
        // 跳转到 TikTok 授权页面
        window.location.href = result.data.authorization_url
      } else {
        state.error = result.error?.message || '发起登录失败'
      }
    } catch (error) {
      console.error('发起登录失败:', error)
      state.error = error.message
    } finally {
      state.loading = false
    }
  },

  /**
   * 处理授权回调
   */
  async handleCallback(code, state_token) {
    console.log('=== Store: 开始处理回调 ===')
    console.log('参数:', { code, state_token })
    
    state.loading = true
    state.error = null
    
    try {
      console.log('调用 API: /api/v1/auth/callback')
      const result = await TikTokAPI.handleCallback(code, state_token)
      
      console.log('API 响应:', result)
      
      if (result.success) {
        console.log('API 调用成功，准备保存令牌')
        console.log('令牌数据:', result.data)
        
        // 保存令牌
        TokenManager.saveTokens(result.data)
        
        console.log('令牌已保存，检查登录状态:', TokenManager.isLoggedIn())
        
        // 获取用户信息
        console.log('准备获取用户信息')
        await this.fetchUserInfo()
        
        console.log('用户信息获取完成:', state.user)
        console.log('最终登录状态:', state.isLoggedIn)
        
        return true
      } else {
        console.error('API 调用失败:', result.error)
        state.error = result.error?.message || '授权回调处理失败'
        return false
      }
    } catch (error) {
      console.error('处理授权回调异常:', error)
      state.error = error.message
      return false
    } finally {
      state.loading = false
      console.log('=== Store: 回调处理结束 ===')
    }
  },

  /**
   * 获取用户信息
   */
  async fetchUserInfo() {
    try {
      const result = await TikTokAPI.getUserInfo()
      
      if (result.success) {
        state.user = result.data
        state.isLoggedIn = true
        TokenManager.saveUserInfo(result.data)
      } else {
        throw new Error(result.error?.message || '获取用户信息失败')
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
      throw error
    }
  },

  /**
   * 刷新令牌
   */
  async refreshToken() {
    const refreshToken = TokenManager.getRefreshToken()
    if (!refreshToken) {
      throw new Error('没有可用的刷新令牌')
    }

    try {
      const result = await TikTokAPI.refreshToken(refreshToken)
      
      if (result.success) {
        TokenManager.saveTokens(result.data)
        return true
      } else {
        throw new Error(result.error?.message || '刷新令牌失败')
      }
    } catch (error) {
      console.error('刷新令牌失败:', error)
      this.logout()
      throw error
    }
  },

  /**
   * 登出
   */
  logout() {
    TokenManager.clearTokens()
    state.user = null
    state.isLoggedIn = false
    state.error = null
  },

  /**
   * 清除错误
   */
  clearError() {
    state.error = null
  },

  /**
   * 检查服务健康状态
   */
  async checkHealth() {
    try {
      const result = await TikTokAPI.checkHealth()
      if (result.success) {
        state.healthStatus = result.data
      }
    } catch (error) {
      console.error('健康检查失败:', error)
    }
  }
}

// 导出状态管理
export const useAuthStore = () => {
  return {
    state,
    getters,
    actions
  }
}

export default useAuthStore 